<template>
    <div class="vfb-field vfb-field--file" :class="classes.wrapper">
        <div class="vfb-field__container" :class="classes.container">
            <FormLabel :id="id" :label="label" :required="required" :className="classes.label" />

            <div class="vfb-file-control" :class="[classes.input, { 'is-disabled': disabled }]">
                <input
                    ref="inputRef"
                    class="vfb-file-input vfb-visually-hidden"
                    :id="id"
                    :name="submitName"
                    type="file"
                    :required="required"
                    :disabled="disabled"
                    :multiple="multiple"
                    :accept="acceptTypes || undefined"
                    :aria-describedby="helpText ? id + '-help-block' : undefined"
                    @change="onChange"
                    :data-validation-rules="validationRules"
                />

                <label :for="id" class="vfb-file-control__trigger">
                    <svg aria-hidden="true" focusable="false" viewBox="0 0 24 24" width="18" height="18"
                         fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M12 16V4M7 9l5-5 5 5M5 20h14" />
                    </svg>
                    <span>{{ uploadLabel }}</span>
                </label>

                <ul v-if="selectedFiles.length > 0" class="vfb-file-list" aria-live="polite">
                    <li v-for="(file, index) in selectedFiles" :key="fileKey(file)" class="vfb-file-list__item">
                        <span class="vfb-file-list__name">{{ file.name }}</span>
                        <button type="button" class="vfb-file-list__remove"
                                :aria-label="removeLabel + ': ' + file.name" :title="removeLabel + ': ' + file.name"
                                :disabled="disabled"
                                @click="removeFile(index)">
                            <svg aria-hidden="true" focusable="false" viewBox="0 0 24 24" width="18" height="18"
                                 fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                <path d="M4 7h16M10 11v6M14 11v6M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2l1-12M9 7V4h6v3" />
                            </svg>
                        </button>
                    </li>
                </ul>
            </div>

            <HelpText v-if="helpText" :helpText="helpText" :id="id + '-help-block'" :className="classes.help" />
        </div>

        <FormErrors class="vfb-errors" :class="classes.errors" />
    </div>
</template>

<script setup lang="ts">
import { computed, nextTick, ref } from 'vue'
import FormLabel from './Label.ce.vue'
import FormErrors from './Errors.ce.vue';
import HelpText from './includes/HelpText.ce.vue';

interface Classes {
    wrapper?: string;
    container?: string;
    input?: string;
    label?: string;
    help?: string;
    errors?: string;
}

interface Props {
    id: string;
    label: string;
    name: string;
    required?: boolean;
    disabled?: boolean;
    helpText?: string;
    classes?: Classes;
    acceptTypes?: string;
    multiple?: boolean;
    maxFiles?: number;
    maxFileSizeKb?: string | number;
    dragAndDrop?: boolean;
    removeLabel?: string;
    uploadLabel?: string;
}

const props = withDefaults(defineProps<Props>(), {
    required: false,
    disabled: false,
    helpText: '',
    classes: () => ({
        wrapper: 'form-group js-form-validation__group',
        container: 'position-relative',
        input: 'form-control',
        label: 'form-label',
        help: '',
        errors: '',
    }),
    acceptTypes: undefined,
    multiple: false,
    maxFiles: undefined,
    maxFileSizeKb: undefined,
    dragAndDrop: false,
    removeLabel: 'Remove',
    uploadLabel: 'Upload file',
});

// PHP collects several files of one input into an array only when the field
// name ends with "[]" — without it $_FILES keeps the LAST part only and every
// other selected file is silently dropped. Repeater members already arrive
// bracketed (`personen[0][cv]`) and simply get the suffix appended.
const submitName = computed(() => {
    if (!props.multiple) {
        return props.name
    }
    return props.name.endsWith('[]') ? props.name : `${props.name}[]`
})

const validationRules = computed(() => {
    const rules: Array<Record<string, number>> = []
    if (props.maxFiles !== null && props.maxFiles !== undefined) {
        rules.push({ maxFiles: Number(props.maxFiles) })
    }
    if (props.maxFileSizeKb !== null && props.maxFileSizeKb !== undefined) {
        rules.push({ maxFileSize: Number(props.maxFileSizeKb) })
    }
    return rules.length > 0 ? JSON.stringify(rules) : undefined
})

const emit = defineEmits<{
    (event: 'update:modelValue', value: File[] | null): void
}>()

// Selected files, listed by name and removable one by one. The native
// input is only visually hidden (still focusable, opens the picker via its
// labels) and stays the source of truth: FormData and the maxFiles/maxFileSize
// validators read input.files, so the list is written back via DataTransfer.
const inputRef = ref<HTMLInputElement | null>(null)
const selectedFiles = ref<File[]>([])
// Set while we dispatch our own change event after a removal, so onChange
// doesn't merge input.files into the list a second time.
let syncing = false

const fileKey = (file: File) => `${file.name}|${file.size}|${file.lastModified}`

// Writes the list back into the native input. Without DataTransfer support
// (very old browsers) the input keeps the browser's own selection.
const syncInput = () => {
    const input = inputRef.value
    if (!input || typeof DataTransfer === 'undefined') {
        return
    }
    const dataTransfer = new DataTransfer()
    selectedFiles.value.forEach(file => dataTransfer.items.add(file))
    input.files = dataTransfer.files
}

const emitValue = () => {
    emit('update:modelValue', selectedFiles.value.length > 0 ? [...selectedFiles.value] : null)
}

const onChange = (e: Event) => {
    if (syncing) {
        return
    }
    const input = e.target as HTMLInputElement
    const picked = input && input.files ? Array.from(input.files) : []

    if (props.multiple) {
        // A second selection adds to the list instead of replacing it; the
        // browser dialog itself can't keep earlier picks.
        const known = new Set(selectedFiles.value.map(fileKey))
        selectedFiles.value = [...selectedFiles.value, ...picked.filter(file => !known.has(fileKey(file)))]
    } else {
        selectedFiles.value = picked
    }

    syncInput()
    emitValue()
}

const removeFile = (index: number) => {
    selectedFiles.value = selectedFiles.value.filter((_, i) => i !== index)
    syncInput()
    emitValue()

    // Re-run validation (maxFiles/maxFileSize/required) on the changed input.
    syncing = true
    inputRef.value?.dispatchEvent(new Event('change', { bubbles: true }))
    syncing = false

    // Keep focus inside the list: next remove button, else the input.
    nextTick(() => {
        const buttons = inputRef.value?.closest('.vfb-file-control')?.querySelectorAll<HTMLButtonElement>('.vfb-file-list__remove')
        const next = buttons && buttons.length > 0 ? buttons[Math.min(index, buttons.length - 1)] : null
        ;(next ?? inputRef.value)?.focus()
    })
}
</script>
